import { Component, OnInit, ViewChild, OnDestroy, Input } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Subscription, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { bitfPromptToDownloadFile } from '@bitf/utils/bitf-files.utils';
import { BitfApiRequestPart } from '@common/libs/bitforce/core/api-call-state/bitf-api-request-part';
// tslint:disable-next-line:max-line-length
import { BitfErrorHandlerService } from '@common/libs/bitforce/core/services/error-handler/bitf-error-handler.service';

import {
  IBitfApiResponse,
  IBitfStoreEvent,
  IBitfBreadcrumbEvent,
  IBitfSortOption,
  IBitfCloseEvent,
  IFsItemActionEvent,
  IBitfOkCancelDialogDataPrompt,
  IBitfApiRequest,
} from '@interfaces';

import {
  EApiCallStateNames,
  EApiRequestPartKeys,
  EFsViewMode,
  eStoreActions,
  EBitfCloseEventStatus,
  EFsAction,
  ERoleMode,
  ERoleActions,
  EFormMode,
} from '@enums';

import { FsItem, Store, FsStore } from '@models';
import {
  LoaderService,
  FsService,
  StoreService,
  ApiCallStateService,
  DialogsService,
  FullSearchService,
} from '@services';
import { CONSTANTS } from '@constants';
import { FsCreateUpdateFilesComponent } from '../fs-create-update-files/fs-create-update-files.component';

@Component({
  selector: 'prada-fs-container',
  templateUrl: './fs-container.component.html',
  styleUrls: ['./fs-container.component.scss'],
})
export class FsContainerComponent implements OnInit, OnDestroy {
  @Input() fsStore: FsStore;

  @ViewChild(CdkVirtualScrollViewport) viewPort: CdkVirtualScrollViewport;
  subscription: Subscription = new Subscription();
  terminateApiCall$ = new Subject();

  ERoleMode = ERoleMode;
  ERoleActions = ERoleActions;

  fsListItemHeight = 50;
  virtualScrollStyle = {};

  itemLayout: EFsViewMode = EFsViewMode.LIST;
  fsItems: FsItem[] = [];

  EFsViewMode = EFsViewMode;
  EApiRequestPartKeys = EApiRequestPartKeys;
  apiCallStateName = EApiCallStateNames.DOCUMENT_ARCHIVE_LIST;
  sortingRequestPart: BitfApiRequestPart;
  paginationRequestPart: BitfApiRequestPart;
  prevPaginationInfo: string;
  paginationInfo = '';
  isLoading = false;
  currentFsItemId: string;
  isVirtualScrollVisible = true;

  isFullTextSearch = false;

  isEnd = false;
  orderByOptions: { [key: string]: IBitfSortOption } = {
    // contentType: {
    //   value: { property: 'contentType', direction: undefined },
    //   label: `${this.translateService.instant('COMMON.LABEL.CONTENT_TYPE')}`,
    // },
    name: {
      value: { property: 'name', direction: undefined },
      label: `${this.translateService.instant('COMMON.LABEL.NAME')}`,
    },
    createdBy: {
      value: { property: 'createdBy', direction: undefined },
      label: `${this.translateService.instant('COMMON.LABEL.CREATED_BY')}`,
    },
    modifiedBy: {
      value: { property: 'modifiedBy', direction: undefined },
      label: `${this.translateService.instant('COMMON.LABEL.MODIFIED_BY')}`,
    },
    modified: {
      value: { property: 'modified', direction: undefined },
      label: `${this.translateService.instant('COMMON.LABEL.MODIFIED')}`,
    },
    created: {
      value: { property: 'created', direction: undefined },
      label: `${this.translateService.instant('COMMON.LABEL.CREATED')}`,
    },
  };

  constructor(
    private loaderService: LoaderService,
    private fsService: FsService,
    private translateService: TranslateService,
    public storeService: StoreService,
    public apiCallStateService: ApiCallStateService,
    private dialogService: DialogsService,
    private bitfErrorHandlerService: BitfErrorHandlerService,
    private fullSearchService: FullSearchService
  ) {
    this.trackByFn = this.trackByFn.bind(this);
  }

  ngOnInit() {
    this.virtualScrollStyle = {
      height: this.fsStore.configuration?.numberOfRows * this.fsListItemHeight + 'px',
    };

    this.apiCallStateName = this.fsStore.configuration.apiCallStateName;

    this.paginationRequestPart = this.apiCallStateService.getRequestPart(
      this.apiCallStateName,
      EApiRequestPartKeys.PAGINATION
    );

    // NOTE: This will force to start from the root folder every time
    this.onBreadcrumbClick({ breadcrumbClicked: undefined, newBreadcrumbs: [] });

    this.subscription = this.apiCallStateService.getStateStore$(this.apiCallStateName).subscribe(() => {
      this.loadData();
    });

    this.subscription.add(
      this.storeService
        .selectStore(`${this.fsStore.configuration.name}.${eStoreActions.UPDATE_FS_BREADCRUMBS}`)
        .subscribe((storeEvent: IBitfStoreEvent<Store>) => {
          this.apiCallStateService.setStore(() => {
            this.paginationRequestPart.reset();
          }, this.apiCallStateName);
        })
    );
  }

  nextBatch() {
    if (this.isEnd) {
      return;
    }
    const renderedItem = this.viewPort.getRenderedRange().end;
    const total = this.viewPort.getDataLength();

    if (renderedItem >= total) {
      if (this.paginationInfo === this.prevPaginationInfo) {
        return;
      }
      this.prevPaginationInfo = this.paginationInfo;
      this.apiCallStateService.setStore(() => {
        this.paginationRequestPart.data = { paginationInfo: this.paginationInfo };
      }, this.apiCallStateName);
    }
  }

  onBreadcrumbClick(breadcrumbEvent: IBitfBreadcrumbEvent) {
    this.fsItems = [];
    this.storeService.setStore((store: Store) => {
      if (this.fsStore.configuration.initialFsItem) {
        this.fsStore.currentItem = this.fsStore.configuration.initialFsItem;
      } else {
        this.fsStore.currentItem = (breadcrumbEvent.breadcrumbClicked as FsItem) || new FsItem();
      }
      this.fsStore.breadcrumbs = breadcrumbEvent.newBreadcrumbs;
    }, `${this.fsStore.configuration.name}.${eStoreActions.UPDATE_FS_BREADCRUMBS}`);
  }

  // NOTE we'll update the store and breadcrumbs when we'll click in the folder item
  // this will trigger a new event catch at line 49 in this file, re loading the list with resetted pagination
  onFsFolderItemClick(fsItem: FsItem) {
    this.fsItems = [];
    this.isVirtualScrollVisible = false;
    this.storeService.setStore((store: Store) => {
      this.fsStore.currentItem = fsItem;
      this.fsStore.breadcrumbs.push(fsItem);
    }, `${this.fsStore.configuration.name}.${eStoreActions.UPDATE_FS_BREADCRUMBS}`);
  }

  onFsItemActionClick(event: IFsItemActionEvent) {
    switch (event.action) {
      case EFsAction.RENAME:
        this.rename(event.fsItem);
        break;
      case EFsAction.DELETE:
        this.deleteFsItem(event.fsItem);
        break;
      case EFsAction.DOWNLOAD:
        this.downloadFsItem(event.fsItem);
        break;
      case EFsAction.TAGS:
        this.updateFileTags(event.fsItem);
        break;
    }
  }

  private downloadFsItem(fsItem: FsItem) {
    this.loaderService.show();
    this.fsService
      .useFs(this.fsStore)
      .download({ id: fsItem.id })
      .subscribe((fileToDownload: HttpResponse<IBitfApiResponse<any>>) => {
        bitfPromptToDownloadFile(fileToDownload.body.originalBody, undefined, undefined, fsItem.name);
      });
  }

  private deleteFsItem(fsItem: FsItem) {
    const ref = this.dialogService.dialog.open(CONSTANTS.okCancelDialogComponent, {
      width: '90%',
      maxWidth: '600px',
      height: 'auto',
      maxHeight: '500px',
      data: {
        title: this.translateService.instant('COMMON.DIALOG.DELETE.TITLE'),
        message: this.translateService.instant('COMMON.DIALOG.DELETE.DESCRIPTION', {
          value: fsItem.name,
        }),
        cancelText: this.translateService.instant('BITF.LABEL.NO'),
        okText: this.translateService.instant('BITF.LABEL.OK'),
      },
    });
    ref.afterClosed().subscribe((result: IBitfCloseEvent<boolean>) => {
      if (result && result.status === EBitfCloseEventStatus.OK) {
        const itemToDeleteIndex = this.fsItems.indexOf(fsItem);
        if (itemToDeleteIndex >= 0) {
          this.fsItems.splice(itemToDeleteIndex, 1);
          this.fsItems = [...this.fsItems];
        }

        this.loaderService.show();
        this.fsService
          .useFs(this.fsStore)
          .delete({ id: fsItem.id })
          .subscribe(
            () => {},
            error => {
              this.bitfErrorHandlerService.handleError(error);
              this.revertDelete(itemToDeleteIndex, fsItem);
            }
          );
      }
    });
  }

  private rename(fsItem: FsItem) {
    const ref = this.dialogService.dialog.open(CONSTANTS.okCancelDialogComponent, {
      width: '90%',
      maxWidth: '400px',
      height: 'auto',
      maxHeight: '500px',
      data: {
        title: this.translateService.instant('COMMON.DIALOG.RENAME.TITLE'),
        prompt: {
          label: this.translateService.instant('COMMON.DIALOG.RENAME.INPUT'),
          isRequired: true,
          value: fsItem.name,
        },
        cancelText: this.translateService.instant('BITF.LABEL.CANCEL'),
        okText: this.translateService.instant('COMMON.LABEL.RENAME'),
      },
    });
    ref.afterClosed().subscribe((result: IBitfCloseEvent<IBitfOkCancelDialogDataPrompt>) => {
      if (result && result.status === EBitfCloseEventStatus.OK) {
        this.loaderService.show();
        const renamedFsItem = new FsItem(fsItem.serialised);
        renamedFsItem.name = result.data.value;
        this.fsService
          .useFs(this.fsStore)
          .patch({
            action: 'rename',
            body: renamedFsItem,
          })
          .subscribe(() => {
            fsItem.name = result.data.value;
          });
      }
    });
  }

  updateFileTags(fsItem: FsItem) {
    const ref = this.dialogService.dialog.open(FsCreateUpdateFilesComponent, {
      width: '90%',
      maxWidth: '800px',
      height: 'auto',
      maxHeight: '600px',
      data: {
        mode: EFormMode.UPDATE,
        fsItem,
        fsStore: this.fsStore,
      },
    });
    ref.afterClosed().subscribe((result: IBitfCloseEvent<FsItem>) => {
      if (result && result.status === EBitfCloseEventStatus.OK) {
        this.loaderService.show();
        this.fsService
          .useFs(this.fsStore)
          .patch({
            body: result.data,
          })
          .subscribe((response: IBitfApiResponse<FsItem>) => {
            Object.assign(fsItem, response.content);
          });
      }
    });
  }

  trackByFn(index) {
    if (this.fsItems.length) {
      return this.fsItems[index].id;
    }
    return -1;
  }

  private revertDelete(index: number, news: FsItem) {
    this.fsItems.splice(index, 0, news);
    this.fsItems = [...this.fsItems];
  }

  private loadData() {
    this.isLoading = true;
    // NOTE: force terminate the previous api call
    this.terminateApiCall$.next();
    const apiCallStateRequest = this.apiCallStateService.getApiRequest(this.apiCallStateName);
    if (apiCallStateRequest.search.fullText) {
      this.advancedSearch(apiCallStateRequest);
    } else {
      this.loadFileSystem(apiCallStateRequest);
    }
  }

  private loadFileSystem(apiCallStateRequest: IBitfApiRequest) {
    if (this.isFullTextSearch) {
      this.resetPagination();
    }
    this.isFullTextSearch = false;
    const fsStoreItemId = this.fsStore.currentItem.id;
    this.fsService
      .useFs(this.fsStore)
      .list<FsItem>({
        id: fsStoreItemId,
        ...apiCallStateRequest,
      })
      .pipe(takeUntil(this.terminateApiCall$))
      .subscribe((response: IBitfApiResponse<FsItem[]>) => {
        this.paginationInfo = response.pagination.paginationInfo;
        this.isEnd = this.paginationInfo === '';
        if (apiCallStateRequest.paginationInfo === '') {
          if (this.viewPort) {
            this.viewPort.scrollToIndex(0);
          }
          this.fsItems = response.content;
        } else {
          this.fsItems = this.fsItems.concat(response.content);
        }
        this.isLoading = false;
        this.isVirtualScrollVisible = true;
      });
  }

  private advancedSearch(apiCallStateRequest: IBitfApiRequest) {
    if (!this.isFullTextSearch) {
      this.resetPagination();
    }
    this.isFullTextSearch = true;
    apiCallStateRequest.query = [...apiCallStateRequest.query.filter(value => value.name !== 'tags')];
    this.fullSearchService
      .post<FsItem[]>({
        ...apiCallStateRequest,
        relation: this.fsStore.configuration?.spDoc,
        isBodyRaw: true,
        modelMapper: 'spsearch',
      })
      .pipe(takeUntil(this.terminateApiCall$))
      .subscribe((response: IBitfApiResponse<FsItem[]>) => {
        this.paginationInfo = (response.pagination && response.pagination.paginationInfo) || '';
        this.isEnd = this.paginationInfo === '';

        if (apiCallStateRequest.paginationInfo === '') {
          if (this.viewPort) {
            this.viewPort.scrollToIndex(0);
          }
          this.fsItems = response.content;
        } else {
          this.fsItems = this.fsItems.concat(response.content);
        }

        this.isLoading = false;
        if (apiCallStateRequest.paginationInfo === '' && this.viewPort) {
          this.viewPort.scrollToIndex(0);
        }
      });
  }

  private resetPagination() {
    this.prevPaginationInfo = undefined;
    this.paginationInfo = undefined;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.terminateApiCall$.complete();
  }
}
